import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import StatusBadge from '../components/StatusBadge';
import { 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Award, 
  Zap, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Sparkles,
  RefreshCw,
  BarChart3,
  PieChart,
  Lightbulb,
  FolderKanban
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#235894', '#26B8A7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const ExecutiveSummary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    fetchExecutiveSummary();
  }, []);

  const fetchExecutiveSummary = async () => {
    try {
      const response = await api.get('/reporting/executive-summary');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch executive summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await api.get('/reporting/executive-summary/ai-insights');
      setAiInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return `${data?.currency || '£'}0`;
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: data?.currency === '$' ? 'USD' : data?.currency === '€' ? 'EUR' : 'GBP',
      maximumFractionDigits: 0 
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  const pipelineData = data?.ideas_pipeline ? Object.entries(data.ideas_pipeline)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({ name: status, value: count })) : [];

  const projectsData = data?.projects_status ? Object.entries(data.projects_status)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({ name: status, value: count })) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="executive-summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Executive Summary
          </h1>
          <p className="text-slate-500 mt-1">
            CI Portfolio Performance Dashboard for Leadership
          </p>
        </div>
        <Button 
          onClick={fetchAIInsights}
          disabled={loadingInsights}
          className="bg-gradient-to-r from-[#235894] to-[#26B8A7] hover:opacity-90"
          data-testid="generate-insights-btn"
        >
          {loadingInsights ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Generate AI Insights
        </Button>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#235894] to-[#1E4B80] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-white/80 text-xs font-medium uppercase tracking-wider mb-1">
              <Target className="w-4 h-4" />
              Expected Savings
            </div>
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">
              {formatCurrency(data?.kpis?.total_expected_savings)}
            </p>
            <p className="text-white/60 text-xs mt-1">Annual target</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#26B8A7] to-[#1fa393] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-white/80 text-xs font-medium uppercase tracking-wider mb-1">
              <Award className="w-4 h-4" />
              Realised Savings
            </div>
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">
              {formatCurrency(data?.kpis?.total_realised_savings)}
            </p>
            <p className="text-white/60 text-xs mt-1">From completed projects</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wider mb-1">
              <TrendingUp className="w-4 h-4" />
              Realization Rate
            </div>
            <p className={`text-3xl font-bold font-['Plus_Jakarta_Sans'] ${
              data?.kpis?.benefit_realization_rate >= 80 ? 'text-emerald-600' : 
              data?.kpis?.benefit_realization_rate >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {data?.kpis?.benefit_realization_rate}%
            </p>
            <p className="text-slate-400 text-xs mt-1">Of expected benefits</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wider mb-1">
              <BarChart3 className="w-4 h-4" />
              Overall ROI
            </div>
            <p className={`text-3xl font-bold font-['Plus_Jakarta_Sans'] ${
              data?.kpis?.overall_roi >= 100 ? 'text-emerald-600' : 
              data?.kpis?.overall_roi >= 0 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {data?.kpis?.overall_roi}%
            </p>
            <p className="text-slate-400 text-xs mt-1">Return on investment</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{data?.kpis?.ideas_in_pipeline}</p>
                <p className="text-xs text-slate-500">Ideas in Pipeline</p>
              </div>
              <Lightbulb className="w-8 h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{data?.kpis?.active_projects}</p>
                <p className="text-xs text-slate-500">Active Projects</p>
              </div>
              <FolderKanban className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{data?.kpis?.blocked_projects}</p>
                <p className="text-xs text-slate-500">Blocked</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{data?.kpis?.completed_projects}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{data?.kpis?.conversion_rate}%</p>
                <p className="text-xs text-slate-500">Conversion Rate</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Insights Panel */}
      {aiInsights && (
        <Card className="border-2 border-[#26B8A7] bg-gradient-to-br from-[#26B8A7]/5 to-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#26B8A7]" />
                <CardTitle>AI-Generated Executive Insights</CardTitle>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchAIInsights}
                disabled={loadingInsights}
              >
                <RefreshCw className={`w-4 h-4 ${loadingInsights ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <CardDescription>
              Generated on {new Date(aiInsights.generated_at).toLocaleString('en-GB')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
              {aiInsights.insights}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Performers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-emerald-500" />
              Top Performers
            </CardTitle>
            <CardDescription>Projects exceeding expectations</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.top_performers?.length > 0 ? (
              <div className="space-y-3">
                {data.top_performers.map((project, index) => (
                  <div 
                    key={project.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 cursor-pointer hover:bg-emerald-100 transition-colors"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{project.name}</p>
                      <p className="text-xs text-slate-500">{project.owner}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-white">
                          {project.achievement_percent}% achieved
                        </Badge>
                        <span className="text-xs text-emerald-600 font-medium">
                          {formatCurrency(project.actual_savings)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Award className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No completed projects yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* At-Risk Initiatives */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              At-Risk Initiatives
            </CardTitle>
            <CardDescription>Requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.at_risk?.length > 0 ? (
              <div className="space-y-3">
                {data.at_risk.map((project, index) => (
                  <div 
                    key={project.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{project.name}</p>
                      <p className="text-xs text-slate-500">{project.owner}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={project.status} />
                        <span className="text-xs text-amber-700">
                          {project.risk_reason}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCurrency(project.expected_savings)} at stake
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-300" />
                <p className="text-sm">No at-risk initiatives</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Wins */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-purple-500" />
              Quick Wins
            </CardTitle>
            <CardDescription>High impact, low effort opportunities</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.quick_wins?.length > 0 ? (
              <div className="space-y-3">
                {data.quick_wins.map((idea, index) => (
                  <div 
                    key={idea.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors"
                    onClick={() => navigate(`/ideas/${idea.id}`)}
                  >
                    <Zap className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{idea.title}</p>
                      <p className="text-xs text-slate-500">{idea.department} • {idea.submitter}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={idea.status} />
                        <span className="text-xs text-purple-700 font-medium">
                          {formatCurrency(idea.expected_savings)} potential
                        </span>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-white">
                          Impact: {idea.impact_score}/5
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-white">
                          Effort: {idea.effort_score}/5
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Zap className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No quick wins identified</p>
                <p className="text-xs mt-1">Ideas need Impact ≥4 & Effort ≤2</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Overview Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ideas Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {pipelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Projects Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {projectsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={projectsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {projectsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Links */}
      <div className="flex flex-wrap gap-4 justify-center pt-4">
        <Button variant="outline" onClick={() => navigate('/ideas')}>
          View All Ideas
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button variant="outline" onClick={() => navigate('/projects')}>
          View All Projects
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button variant="outline" onClick={() => navigate('/reporting')}>
          Detailed Reporting
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
